import React, { useEffect, useState } from 'react';
import axios from 'axios';
import styles from './Aptable.module.css';
import config from '../../config/config';

const Aptable = () => {
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = () => {
    setIsLoading(true);
    axios.get(`${config.apiBaseUrl}/api/products`)
      .then(res => {
        setProducts(res.data.map(p => ({ ...p, showDetails: false })));
        setIsLoading(false);
      })
      .catch(err => {
        console.error(err);
        setIsLoading(false);
      });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await axios.delete(`${config.apiBaseUrl}/api/products/${id}`);
        fetchProducts();
      } catch (err) {
        console.error('Delete failed:', err);
      }
    }
  };

  const handleEdit = (product) => {
    setEditingProduct({ ...product, newImages: [] });
    setShowModal(true);
  };

  const handleEditSave = async () => {
    try {
      const formData = new FormData();

      // Add basic fields
      formData.append('productName', editingProduct.productName);
      formData.append('productDetails', editingProduct.productDetails);
      formData.append('benefits', editingProduct.benefits);
      formData.append('weightValue', editingProduct.weight?.value || '');
      formData.append('weightUnit', editingProduct.weight?.unit || 'g');
      
      // Add JSON-stringified fields
      formData.append('pdprice', JSON.stringify({
        npr: editingProduct.pdprice?.npr || 0,
        inr: editingProduct.pdprice?.inr || 0,
        usd: editingProduct.pdprice?.usd || 0
      }));
      formData.append('categories', JSON.stringify(editingProduct.categories || []));
      formData.append('tags', JSON.stringify(editingProduct.tags || []));

      // Handle images
      const existingImages = (editingProduct.images || []).map(img => ({
        url: img.url || img,
        public_id: img.public_id || img.split('/').pop().split('.')[0]
      }));
      formData.append('existingImages', JSON.stringify(existingImages));

      // Add new images
      if (editingProduct.newImages) {
        editingProduct.newImages.forEach(file => {
          formData.append('images', file);
        });
      }

      await axios.put(
        `${config.apiBaseUrl}/api/products/${editingProduct._id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setShowModal(false);
      fetchProducts();
    } catch (err) {
      console.error('Detailed error:', {
        message: err.message,
        response: err.response?.data,
        stack: err.stack
      });
      alert(`Update failed: ${err.response?.data?.error || err.message}`);
    }
  };

  const toggleDetails = (id) => {
    setProducts(prevProducts =>
      prevProducts.map(p =>
        p._id === id ? { ...p, showDetails: !p.showDetails } : p
      )
    );
  };

  const filteredProducts = products.filter(product =>
    product.productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.productDetails.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (product.tags && product.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
  );

  return (
  
    <div className={styles.container}>
      
      <div className={styles.header}>
        <h2>Product Management</h2>
        <div className={styles.searchContainer}>
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
          <span className={styles.searchIcon}>🔍</span>
        </div>
        <div className={styles.totalProducts}>
          Total Products: <span>{products.length}</span>
        </div>
      </div>

      {isLoading ? (
        <div className={styles.loader}>
          <div className={styles.spinner}></div>
          <p>Loading products...</p>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.snCol}>#</th>
                <th className={styles.nameCol}>Product Name</th>
                <th className={styles.priceCol}>Price (NPR)</th>
                <th className={styles.actionsCol}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan="4" className={styles.noResults}>
                    No products found matching your search.
                  </td>
                </tr>
              ) : (
                filteredProducts.map((p, i) => (
                  <React.Fragment key={p._id}>
                    <tr className={p.showDetails ? styles.expandedRow : ''}>
                      <td>{i + 1}</td>
                      <td 
                        onClick={() => toggleDetails(p._id)} 
                        className={styles.clickable}
                      >
                        <div className={styles.productName}>
                          {p.productName}
                          <span className={styles.toggleIcon}>
                            {p.showDetails ? '▲' : '▼'}
                          </span>
                        </div>
                        {p.tags && p.tags.length > 0 && (
                          <div className={styles.tags}>
                            {p.tags.map((tag, idx) => (
                              <span key={idx} className={styles.tag}>{tag}</span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td>रु{p.pdprice?.npr || 'N/A'}</td>
                      <td className={styles.actions}>
                        <button 
                          className={`${styles.btn} ${styles.editBtn}`} 
                          onClick={() => handleEdit(p)}
                        >
                          Edit
                        </button>
                        <button 
                          className={`${styles.btn} ${styles.delBtn}`} 
                          onClick={() => handleDelete(p._id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                    {p.showDetails && (
                      <tr className={styles.detailsRow}>
                        <td colSpan={4}>
                          <div className={styles.detailsContainer}>
                            <div className={styles.detailsSection}>
                              <h4>Details</h4>
                              <p>{p.productDetails}</p>
                            </div>
                            <div className={styles.detailsSection}>
                              <h4>Benefits</h4>
                              <p>{p.benefits}</p>
                            </div>
                            <div className={styles.priceSection}>
                              <h4>Prices</h4>
                              <div className={styles.priceGrid}>
                                <div>
                                  <span className={styles.currency}>NPR</span>
                                  <span className={styles.price}>रु{p.pdprice?.npr || 'N/A'}</span>
                                </div>
                                <div>
                                  <span className={styles.currency}>INR</span>
                                  <span className={styles.price}>₹{p.pdprice?.inr || 'N/A'}</span>
                                </div>
                                <div>
                                  <span className={styles.currency}>USD</span>
                                  <span className={styles.price}>${p.pdprice?.usd || 'N/A'}</span>
                                </div>
                              </div>
                            </div>
                            {p.images && p.images.length > 0 && (
                              <div className={styles.imagesSection}>
                                <h4>Images</h4>
                                <div className={styles.images}>
                                  {p.images.map((img, idx) => (
                                    <div key={idx} className={styles.imageContainer}>
                                      <img 
                                        src={img.url || img} 
                                        alt={`Product ${idx + 1}`} 
                                        className={styles.productImage}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && editingProduct && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>Edit Product</h2>
              <button 
                onClick={() => setShowModal(false)} 
                className={styles.closeBtn}
              >
                &times;
              </button>
            </div>
            
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>Product Name</label>
                <input
                  type="text"
                  value={editingProduct.productName || ''}
                  onChange={e => setEditingProduct({ ...editingProduct, productName: e.target.value })}
                  placeholder="Enter product name"
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Product Details</label>
                <textarea
                  rows={3}
                  value={editingProduct.productDetails || ''}
                  onChange={e => setEditingProduct({ ...editingProduct, productDetails: e.target.value })}
                  placeholder="Enter product details"
                  className={styles.formTextarea}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Benefits</label>
                <textarea
                  rows={2}
                  value={editingProduct.benefits || ''}
                  onChange={e => setEditingProduct({ ...editingProduct, benefits: e.target.value })}
                  placeholder="Enter product benefits"
                  className={styles.formTextarea}
                />
              </div>

              <div className={styles.priceSection}>
                <h4>Prices</h4>
                <div className={styles.priceGrid}>
                  <div className={styles.formGroup}>
                    <label>NPR</label>
                    <input
                      type="number"
                      placeholder="NPR Price"
                      value={editingProduct.pdprice?.npr || ''}
                      onChange={e => setEditingProduct({
                        ...editingProduct,
                        pdprice: { ...editingProduct.pdprice, npr: e.target.value }
                      })}
                      className={styles.formInput}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>INR</label>
                    <input
                      type="number"
                      placeholder="INR Price"
                      value={editingProduct.pdprice?.inr || ''}
                      onChange={e => setEditingProduct({
                        ...editingProduct,
                        pdprice: { ...editingProduct.pdprice, inr: e.target.value }
                      })}
                      className={styles.formInput}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>USD</label>
                    <input
                      type="number"
                      placeholder="USD Price"
                      value={editingProduct.pdprice?.usd || ''}
                      onChange={e => setEditingProduct({
                        ...editingProduct,
                        pdprice: { ...editingProduct.pdprice, usd: e.target.value }
                      })}
                      className={styles.formInput}
                    />
                  </div>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Tags (comma separated)</label>
                <input
                  type="text"
                  placeholder="e.g., organic, handmade, herbal"
                  value={editingProduct.tags?.join(', ') || ''}
                  onChange={e => setEditingProduct({
                    ...editingProduct,
                    tags: e.target.value.split(',').map(t => t.trim())
                  })}
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Categories (comma separated)</label>
                <input
                  type="text"
                  placeholder="e.g., skincare, haircare, wellness"
                  value={editingProduct.categories?.join(', ') || ''}
                  onChange={e => setEditingProduct({
                    ...editingProduct,
                    categories: e.target.value.split(',').map(c => c.trim())
                  })}
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Weight</label>
                <div className={styles.weightInput}>
                  <input
                    type="number"
                    placeholder="Value"
                    value={editingProduct.weight?.value || ''}
                    onChange={e => setEditingProduct({
                      ...editingProduct,
                      weight: { ...editingProduct.weight, value: e.target.value }
                    })}
                    className={styles.formInput}
                  />
                  <select
                    value={editingProduct.weight?.unit || 'g'}
                    onChange={e => setEditingProduct({
                      ...editingProduct,
                      weight: { ...editingProduct.weight, unit: e.target.value }
                    })}
                    className={styles.formSelect}
                  >
                    <option value="g">g</option>
                    <option value="kg">kg</option>
                    <option value="ml">ml</option>
                    <option value="l">l</option>
                    <option value="pcs">pcs</option>
                  </select>
                </div>
              </div>

              {editingProduct.images?.length > 0 && (
                <div className={styles.formGroup}>
                  <label>Current Images</label>
                  <div className={styles.currentImages}>
                    {editingProduct.images.map((img, idx) => (
                      <div key={idx} className={styles.imagePreview}>
                        <img src={img.url || img} alt={`Preview ${idx}`} />
                        <button
                          onClick={() => {
                            const updatedImages = [...editingProduct.images];
                            updatedImages.splice(idx, 1);
                            setEditingProduct({ ...editingProduct, images: updatedImages });
                          }}
                          className={styles.removeImageBtn}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className={styles.formGroup}>
                <label>Add More Images</label>
                <div className={styles.fileUpload}>
                  <label className={styles.uploadBtn}>
                    Choose Files
                    <input
                      type="file"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files);
                        setEditingProduct({
                          ...editingProduct,
                          newImages: [...(editingProduct.newImages || []), ...files]
                        });
                      }}
                      className={styles.fileInput}
                    />
                  </label>
                  {editingProduct.newImages?.length > 0 && (
                    <div className={styles.newImagesPreview}>
                      {editingProduct.newImages.map((file, idx) => (
                        <div key={idx} className={styles.newImageItem}>
                          {file.name}
                          <button
                            onClick={() => {
                              const updatedNewImages = [...editingProduct.newImages];
                              updatedNewImages.splice(idx, 1);
                              setEditingProduct({ ...editingProduct, newImages: updatedNewImages });
                            }}
                            className={styles.removeNewImageBtn}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button 
                className={`${styles.btn} ${styles.cancelBtn}`} 
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button 
                className={`${styles.btn} ${styles.saveBtn}`} 
                onClick={handleEditSave}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
   
  );
};

export default Aptable;